import { getTimeFromStr } from './functions';

const isDataUrlImage = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('data:image/');

const removeUndefinedFields = <T extends Record<string, unknown>>(value: T): T => {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
};

const coerceTimeIndexToNumber = (value: unknown): number | null | undefined => {
  if (value === null || value === undefined || value === '') {
    return value as null | undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }
  if (!trimmed.includes(':')) {
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  const parts = trimmed.split(':').map(Number);
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !Number.isFinite(part))) {
    return undefined;
  }
  const [hours, minutes, seconds] =
    parts.length === 3 ? parts : [0, parts[0], parts[1]];
  return hours * 3600 + minutes * 60 + seconds;
};

const normalizeContextTimeFields = (
  context: Record<string, unknown>,
): Record<string, unknown> =>
  removeUndefinedFields({
    ...context,
    startTime: coerceTimeIndexToNumber(context.startTime),
    endTime: coerceTimeIndexToNumber(context.endTime),
  });

const stripStillFrameThumbnailFromContext = (
  context: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!context || typeof context !== 'object') {
    return {};
  }

  const { stillFrame: _stillFrame, canvas, ...rest } = context;
  if (!canvas || typeof canvas !== 'object' || Array.isArray(canvas)) {
    return normalizeContextTimeFields(rest);
  }

  const {
    stillFrame: _canvasStillFrame,
    stillFrameImage: _canvasStillFrameImage,
    ...canvasRest
  } = canvas as Record<string, unknown>;
  return normalizeContextTimeFields(removeUndefinedFields({
    ...rest,
    canvas: Object.keys(canvasRest).length > 0 ? canvasRest : undefined,
  }));
};

/** Data URLs and storage/proxy refs — not bare time indices stored in legacy ``stillFrame``. */
const isStillFrameImageRef = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (isDataUrlImage(trimmed)) return true;
  if (/^s3:\/\//i.test(trimmed)) return true;
  if (/^files\//i.test(trimmed)) return true;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (trimmed.startsWith('/api/proxy/')) return true;
  return false;
};

const isPersistableStillFrameImage = isStillFrameImageRef;

const normalizeMediaStillFrameContext = (
  context: Record<string, unknown> | null | undefined,
  fallbackStillFrameImage?: string,
): Record<string, unknown> | null => {
  if (!context || typeof context !== 'object') {
    return fallbackStillFrameImage ? { canvas: { stillFrameImage: fallbackStillFrameImage } } : context ?? null;
  }

  const { canvas, stillFrame, ...rest } = context;
  const canvasObject =
    canvas && typeof canvas === 'object' && !Array.isArray(canvas)
      ? { ...(canvas as Record<string, unknown>) }
      : {};
  const legacyStillFrameImage = isDataUrlImage(stillFrame) ? stillFrame : undefined;
  const stillFrameValue = isDataUrlImage(stillFrame) ? undefined : stillFrame;
  // Prefer an already-persisted storage URI on the media row. Previously only
  // data-URL thumbnails were kept, so junkdrawer ``s3://`` still frames were
  // stripped on every save and missing after reopen.
  const existingStillFrameImage = isPersistableStillFrameImage(canvasObject.stillFrameImage)
    ? canvasObject.stillFrameImage.trim()
    : undefined;
  const stillFrameImage =
    existingStillFrameImage ?? legacyStillFrameImage ?? fallbackStillFrameImage;

  delete canvasObject.stillFrame;
  delete canvasObject.stillFrameImage;
  const normalizedCanvas = removeUndefinedFields({
    ...canvasObject,
    stillFrameImage,
  });

  return normalizeContextTimeFields(removeUndefinedFields({
    ...rest,
    stillFrame: stillFrameValue,
    canvas: Object.keys(normalizedCanvas).length > 0 ? normalizedCanvas : undefined,
  }));
};

const getContextStillFrameImage = (
  context: Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!context || typeof context !== 'object') {
    return undefined;
  }
  const canvas = context.canvas;
  if (canvas && typeof canvas === 'object' && !Array.isArray(canvas)) {
    const stillFrameImage = (canvas as Record<string, unknown>).stillFrameImage;
    if (isPersistableStillFrameImage(stillFrameImage)) {
      return stillFrameImage.trim();
    }
  }
  // Legacy top-level stillFrame: image ref or data URL only (never time indices).
  if (isStillFrameImageRef(context.stillFrame)) {
    return context.stillFrame.trim();
  }
  return undefined;
};

const contextPage = (context: Record<string, unknown> | null | undefined): unknown =>
  context?.pageNumber ?? context?.page;

const contextStartTime = (context: Record<string, unknown> | null | undefined): unknown =>
  coerceTimeIndexToNumber(context?.startTime);

const buildStillFrameMigrationKeys = (item: Record<string, unknown>, contextKey: 'mediaContext' | 'sourceContext'): string[] => {
  const context = item[contextKey] as Record<string, unknown> | null | undefined;
  const page = contextPage(context) ?? item.pageNumber ?? '';
  const startTime = contextStartTime(context) ?? '';
  const urls = [
    item.id,
    item.sourceId,
    item.sourceID,
    item.mediaSourceId,
    item.mediaSourceID,
    item.sourceUrl,
    item.sourceFilePath,
    item.mediaUrl,
    item.mediaSrc,
    item.srcToUse,
    item.previewFilePath,
    item.originalUrl,
    item.s3Url,
  ].filter((value): value is string | number => typeof value === 'string' || typeof value === 'number');

  return Array.from(new Set(urls.map((url) => `${url}|${page}|${startTime}`)));
};

const buildLegacySourceStillFrameImageByIdentity = (
  sources: Record<string, unknown>[],
): Map<unknown, string> => {
  const imagesByIdentity = new Map<unknown, string>();
  for (const source of sources) {
    const stillFrameImage = getContextStillFrameImage(source.sourceContext as Record<string, unknown> | null | undefined);
    if (!stillFrameImage) {
      continue;
    }
    for (const identity of buildStillFrameMigrationKeys(source, 'sourceContext')) {
      if (!imagesByIdentity.has(identity)) {
        imagesByIdentity.set(identity, stillFrameImage);
      }
    }
  }
  return imagesByIdentity;
};

export const stripStillFrameThumbnailFromSource = <T extends Record<string, unknown>>(source: T): T => {
  const sourceContext =
    source.sourceContext && typeof source.sourceContext === 'object'
      ? stripStillFrameThumbnailFromContext(source.sourceContext as Record<string, unknown>)
      : source.sourceContext;
  return {
    ...source,
    sourceContext,
  };
};

export const normalizeMediaStillFrame = <T extends Record<string, unknown>>(media: T, fallbackStillFrameImage?: string): T => {
  return {
    ...media,
    mediaContext: normalizeMediaStillFrameContext(
      media.mediaContext as Record<string, unknown> | null | undefined,
      fallbackStillFrameImage,
    ),
  };
};

export const normalizeTimelineEventStillFrames = <T extends Record<string, unknown>>(event: T): T => {
  const sources = Array.isArray(event.sources)
    ? event.sources as Record<string, unknown>[]
    : undefined;
  const legacySourceStillFrameImages = sources
    ? buildLegacySourceStillFrameImageByIdentity(sources)
    : undefined;

  return {
    ...event,
    sources: sources
      ? sources.map(stripStillFrameThumbnailFromSource)
      : event.sources,
    media: Array.isArray(event.media)
      ? (event.media as Record<string, unknown>[]).map((media) => {
        const fallbackStillFrameImage = buildStillFrameMigrationKeys(media, 'mediaContext')
          .map((identity) => legacySourceStillFrameImages?.get(identity))
          .find(isPersistableStillFrameImage);
        return normalizeMediaStillFrame(media, fallbackStillFrameImage);
      })
      : event.media,
  };
};

const constructEventDateTime = (event: Record<string, unknown>): string | null => {
  const dateIn = event.date;
  const timeIn = event.time;
  if (!dateIn) return null;
  const date = new Date(dateIn as string | number | Date);
  if (timeIn) {
    const { hoursNumber, minutesNumber, secondsNumber } = getTimeFromStr(timeIn as string | null);
    if (hoursNumber !== undefined && minutesNumber !== undefined && secondsNumber !== undefined) {
      const hoursString = hoursNumber > 9 ? hoursNumber.toString() : `0${hoursNumber}`;
      const minutesString = minutesNumber > 9 ? minutesNumber.toString() : `0${minutesNumber}`;
      const secondsString = secondsNumber > 9 ? secondsNumber.toString() : `0${secondsNumber}`;
      const dateStrIn = new Date(dateIn as string | number | Date).toISOString();
      const dateStr = `${dateStrIn.split('T')[0]}T${hoursString}:${minutesString}:${secondsString}.000Z`;
      return dateStr;
    }
    return date.toISOString();
  }
  return date.toISOString();
};

/**
 * Copy citation ``content`` from content-only JSON media onto matching sources
 * that lost it (events saved before source ``content`` was persisted).
 *
 * Only hydrates when both sides are unambiguous for that canonical path:
 * exactly one source missing content at the path, and exactly one JSON media
 * row with content at the same path. Shared-path sibling citations are left
 * alone rather than given the wrong excerpt.
 */
export function hydrateSourcesContentFromMedia<T extends Record<string, unknown>>(
  sources: T[],
  media: Array<Record<string, unknown>> | null | undefined,
): T[] {
  if (!sources?.length || !media?.length) return sources || [];

  const pathOf = (row: Record<string, unknown>): string =>
    String(row.sourceUrl || row.sourceFilePath || '').trim();

  const needyCountByPath = new Map<string, number>();
  for (const source of sources) {
    if (typeof source.content === 'string' && source.content.trim() !== '') continue;
    const path = pathOf(source);
    if (!path) continue;
    needyCountByPath.set(path, (needyCountByPath.get(path) || 0) + 1);
  }

  const mediaByPath = new Map<string, Record<string, unknown>[]>();
  for (const m of media) {
    if (m.mediaType !== 'json') continue;
    if (typeof m.content !== 'string' || m.content.trim() === '') continue;
    const path = pathOf(m);
    if (!path) continue;
    const list = mediaByPath.get(path) || [];
    list.push(m);
    mediaByPath.set(path, list);
  }

  return sources.map((source) => {
    if (typeof source.content === 'string' && source.content.trim() !== '') {
      return source;
    }
    const sourcePath = pathOf(source);
    if (!sourcePath) return source;
    if ((needyCountByPath.get(sourcePath) || 0) !== 1) return source;
    const candidates = mediaByPath.get(sourcePath) || [];
    if (candidates.length !== 1) return source;
    return { ...source, content: candidates[0].content };
  });
}

const resolveTimelineEventApproved = (event: Record<string, unknown>): boolean =>
  event.approved === true || event.approval === 'approved' || event.approval === true;

const trimmedField = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * Carry the path of the source a media row renders inside ``mediaContext``.
 *
 * A media row's ``mediaUrl`` names a *processed* object — ``…/archive_processing/…``
 * for a zip member, or the obfuscated rendition of a moderated image — which
 * shares no path with the ``…/detect-and-protect.zip/test-data/…`` ``sourceUrl``
 * its Displayed Sources row stores. In memory the row also carries ``sourceUrl``
 * / ``sourceFilePath``, and that is what pairs the two, but timeline-service's
 * media model persists only ``mediaType``, ``mediaUrl`` and ``mediaContext`` —
 * the top-level pair sent below is accepted and dropped. Reopening the event
 * then finds a media row matching no source, and
 * ``mergeMissingMediaSourcesIntoList`` invents a second Displayed Sources row
 * out of the rendition, labelled ``…_obfuscated.jpg`` for a moderated file
 * (TR-2153).
 *
 * ``mediaContext`` is free-form and round-trips untouched, so the link rides
 * there. It is *identity only*: nothing may promote it back onto the row's top
 * level, where ``getSrcToUse`` prefers a bare relative path over the playable
 * ``mediaUrl`` and leaves players spinning on a URL that resolves to nothing.
 */
const withMediaSourceIdentity = (
  media: Record<string, unknown>,
  mediaContext: Record<string, unknown> | null,
): Record<string, unknown> | null => {
  const sourceFilePath = trimmedField(media.sourceFilePath);
  const sourceUrl = trimmedField(media.sourceUrl) || sourceFilePath;
  if (!sourceUrl) return mediaContext;
  return removeUndefinedFields({
    ...(mediaContext ?? {}),
    sourceUrl,
    sourceFilePath: sourceFilePath || undefined,
  });
};

/** Maps UI slide/event shape to timeline-service API payload (same as legacy BFF). */
export function mapSlideToTimelineEventPayload(event: Record<string, unknown>): Record<string, unknown> {
  event = normalizeTimelineEventStillFrames(event);
  let compatibleMedia: Record<string, unknown>[] = [];
  if (Array.isArray(event.media)) {
    compatibleMedia = (event.media as Record<string, unknown>[]).map((m) => ({
      mediaType: m.mediaType || 'Unknown',
      mediaUrl: (m.s3Url || m.mediaUrl || '') as string,
      mediaContext: withMediaSourceIdentity(
        m,
        (m.mediaContext as Record<string, unknown> | null | undefined) ?? null,
      ),
      // Citation excerpt for JSON↔markdown toggle after reload.
      ...(typeof m.content === 'string' && m.content.trim() !== ''
        ? { content: m.content }
        : {}),
      // Canonical path for content-only JSON (empty mediaUrl) identity after reload.
      ...(typeof m.sourceUrl === 'string' && m.sourceUrl.trim() !== ''
        ? { sourceUrl: m.sourceUrl }
        : typeof m.sourceFilePath === 'string' && m.sourceFilePath.trim() !== ''
          ? { sourceUrl: m.sourceFilePath }
          : {}),
      ...(typeof m.sourceFilePath === 'string' && m.sourceFilePath.trim() !== ''
        ? { sourceFilePath: m.sourceFilePath }
        : {}),
    }));
  }
  let compatibleSources: Record<string, unknown>[] = [];
  if (Array.isArray(event.sources)) {
    compatibleSources = (event.sources as Record<string, unknown>[]).map((s) => {
      const mediaUrl =
        typeof s.mediaUrl === 'string' && s.mediaUrl.trim() !== ''
          ? s.mediaUrl
          : typeof s.mediaSrc === 'string' && s.mediaSrc.trim() !== ''
            ? s.mediaSrc
            : '';
      return {
        sourceType: s.sourceType || 'Unknown',
        sourceUrl: (s.sourceUrl || '') as string,
        pageNumber: s.pageNumber ?? null,
        sourceContext: s.sourceContext && typeof s.sourceContext === 'object' ? s.sourceContext : {},
        // Inline JSON citation excerpt — required for content-only identity / media link.
        ...(typeof s.content === 'string' && s.content.trim() !== ''
          ? { content: s.content }
          : {}),
        // Preserve resolved display URL / type so reload can URL-match media rows.
        ...(mediaUrl ? { mediaUrl, mediaSrc: mediaUrl } : {}),
        ...(typeof s.mediaType === 'string' && s.mediaType.trim() !== ''
          ? { mediaType: s.mediaType }
          : {}),
      };
    });
  }
  const loc = event.location as Record<string, unknown> | undefined;
  const compatibleLocation = loc
    ? {
        latitude: (loc.latitude as number) || 0,
        longitude: (loc.longitude as number) || 0,
        address1: (loc.address1 as string) || '',
        address2: (loc.address2 as string) || '',
        city: (loc.city as string) || '',
        country: (loc.country as string) || '',
        state: (loc.state as string) || '',
        zipCode: (loc.zipCode as string) || '',
      }
    : null;
  return {
    eventDateTime: event.date ? constructEventDateTime(event) : null,
    eventLocalTimezone: event.eventLocalTimezone ?? null,
    isTimeValid: event.isTimeValid === true,
    slideTitle: event.slideTitle ?? null,
    slideSubTitle: event.slideSubTitle ?? null,
    slideDescription: event.slideDescription ?? null,
    courtroomTitle: event.courtroomTitle ?? null,
    courtroomDescription: event.courtroomDescription ?? null,
    location: compatibleLocation,
    sources: compatibleSources,
    media: compatibleMedia,
    hidden: event.show === false,
    // Hidden from the horizontal timeline view only (independent of `hidden`).
    // Forward-compatible: persists once the timeline-service stores this field;
    // until then a localStorage fallback keeps the toggle working client-side.
    hiddenFromTimeline: event.hideFromTimeline === true,
    approved: resolveTimelineEventApproved(event),
    deleted: false,
  };
}
